// src/pages/nfl/NflProjections.jsx

import React, { useEffect, useMemo, useState } from "react";

import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, ReferenceLine } from "recharts";

const SOURCE = "/data/nfl/classic/latest/playersimulations.json";

const SUPPLEMENTAL_PROJECTIONS = "/data/nfl/classic/latest/projections.json";

const META = "/data/nfl/classic/latest/meta.json";

const SITE_IDS = "/data/nfl/classic/latest/site_ids.json";

const VEGAS = "/data/nfl/classic/latest/vegas.json";

const POINT_DISTS = "/data/nfl/latest/point_distributions.json";

const SLATES = [ { id: "main", label: "Main" }, { id: "early", label: "Early" }, { id: "late", label: "Late" }, { id: "thu_mon", label: "Thu–Mon" } ];

const POSITIONS = ["QB", "RB", "WR", "TE", "DST"];

const SITES = { dk: { label: "DK", logo: "/logos/dk.png" }, fd: { label: "FD", logo: "/logos/fd.png" }, both: { label: "Both" } };

const COLS_COMMON = [

  { key: "player", label: "Player", type: "text", w: "min-w-[5rem]" },

  { key: "pos", label: "Pos", type: "text" },

  { key: "team", label: "Team", type: "text" },

  { key: "opp", label: "Opp", type: "text" },

];

const siteColumns = (site) => [

  ["sal", "Sal", "money"], ["proj", "Proj", "num1"], ["val", "Val", "num1"],

  ["pown", "pOWN%", "pct"], ["floor", "Floor", "num1"], ["ceiling", "Ceil", "num1"],

  ["opt", "Opt%", "pct"], ["lev", "Lev%", "pct"], ["rtg", "Rtg", "num1-force"],

].map(([key, label, type]) => ({ key: `${site}_${key}`, label: `${site.toUpperCase()} ${label}`, type }));

const COLS_DK = siteColumns("dk");

const COLS_FD = siteColumns("fd");

const num = (v) => {

  if (v == null || String(v).trim() === "") return null;

  const n = Number(String(v).replace(/[,$%\s]/g, ""));

  return Number.isFinite(n) ? n : null;

};

const fmt0 = (v) => num(v) == null ? "" : num(v).toLocaleString();

const fmt1 = (v) => num(v) == null ? "" : num(v).toFixed(1);

// Projection percentages are decimal fractions, as in the original page.

const fmtPct = (v) => num(v) == null ? "" : `${(num(v) * 100).toFixed(1)}%`;

const canonicalTeam = (v) => String(v || "").trim().toUpperCase().replace(/^@/, "").replace(/^JAC$/, "JAX");

const position = (v) => {

  const p = String(v || "").trim().toUpperCase().split("/")[0].replace(/\d+$/, "");

  return ["D", "DEF"].includes(p) ? "DST" : p;

};

const normalizeName = (v) => String(v || "").toLowerCase().normalize("NFKD")

  .replace(/[\u0300-\u036f]/g, "").replace(/[.'’,-]/g, " ")

  .replace(/\s+(jr|sr|ii|iii|iv)\b/g, "").replace(/\s+/g, " ").trim();

const gameKey = (row) => row.team && row.opp ? [row.team, row.opp].sort().join("@") : "";

const playerKey = (row) => `${canonicalTeam(row.team)}|${normalizeName(row.player)}|${position(row.pos)}`;

const teamLogo = (team) => `/logos/nfl/${canonicalTeam(team)}.png`;

const queryChoice = (key, allowed, fallback) => {

  const value = new URLSearchParams(window.location.search).get(key);

  return allowed.includes(value) ? value : fallback;

};

function asRows(data) {

  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.rows)) return data.rows;

  return ["dk", "fd"].flatMap((site) =>

    (Array.isArray(data?.[site]) ? data[site] : []).map((row) => ({ ...row, site })));

}

function useAnyJson(url) {

  const [data, setData] = useState(null);

  const [loading, setLoading] = useState(true);

  const [err, setErr] = useState("");

  useEffect(() => {

    const controller = new AbortController();

    let alive = true;

    setLoading(true);

    setErr("");

    (async () => {

      try {

        const response = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store", signal: controller.signal });

        if (!response.ok) throw new Error(`${url}: HTTP ${response.status}`);

        const json = await response.json();

        if (alive) setData(json);

      } catch (error) {

        if (alive) setErr(String(error));

      } finally {

        if (alive) setLoading(false);

      }

    })();

    return () => { alive = false; controller.abort(); };

  }, [url]);

  return { data, loading, err };

}

function useJson(url) {

  const result = useAnyJson(url);

  const rows = useMemo(() => asRows(result.data), [result.data]);

  return { rows, loading: result.loading, err: result.err };

}

// Join by name/team/position, then use site IDs to establish slate eligibility.

function wireRows(simulations, siteRows, vegasRows, slate, selectedSite, supplementalRows = []) {

  // Supplemental matches enrich metrics only; they never define the player pool.

  const extras = new Map();

  const defenseExtras = new Map();

  for (const row of supplementalRows) {

    extras.set(playerKey(row), row);

    if (position(row.pos) === "DST") defenseExtras.set(canonicalTeam(row.team), row);

  }

  const idKey = slate === "main" ? "id" : `${slate}_id`;

  const salaryKey = slate === "main" ? "salary" : `${slate}_salary`;

  const full = new Map();

  const byName = new Map();

  const defenses = new Map();

  const nameTeams = new Map();

  for (const entry of siteRows) {

    const site = String(entry.site || "").toLowerCase();

    const team = canonicalTeam(entry.team);

    const pos = position(entry.pos);

    const name = normalizeName(entry.name ?? entry.player);

    full.set(`${site}|${name}|${team}|${pos}`, entry);

    const nameKey = `${site}|${name}|${pos}`;

    byName.set(nameKey, [...(byName.get(nameKey) || []), entry]);

    if (pos === "DST") defenses.set(`${site}|${team}`, entry);

    const teams = nameTeams.get(`${name}|${pos}`) || new Set();

    if (team) teams.add(team);

    nameTeams.set(`${name}|${pos}`, teams);

  }

  const opponents = new Map(vegasRows.map((r) => [canonicalTeam(r.team), canonicalTeam(r.opponent ?? r.opp)]));

  const eligible = (entry) => entry && String(entry[idKey] ?? "").trim() !== ""

    && num(entry[salaryKey]) > 0 && num(entry[salaryKey]) < 50000;

  return simulations.map((row) => {

    const name = normalizeName(row.player);

    const pos = position(row.pos);

    let team = canonicalTeam(row.team);

    if (!team) {

      const candidates = nameTeams.get(`${name}|${pos}`);

      if (candidates?.size === 1) team = [...candidates][0];

    }

    const extra = extras.get(playerKey({ ...row, team, pos }))

      || (pos === "DST" ? defenseExtras.get(team) : null) || {};

    const merged = { ...row, team, pos, opp: opponents.get(team) || canonicalTeam(row.opp) || canonicalTeam(extra.opp) };

    // Simulations remain authoritative. Only absent metrics receive a fallback.

    for (const site of ["dk", "fd"]) {

      for (const metric of ["proj", "pown", "floor", "ceiling", "opt", "lev", "rtg"]) {

        const key = `${site}_${metric}`;

        if (merged[key] == null || String(merged[key]).trim() === "") merged[key] = extra[key] ?? null;

      }

    }

    let included = false;

    for (const site of ["dk", "fd"]) {

      let entry = full.get(`${site}|${name}|${team}|${pos}`);

      if (!entry && pos === "DST") entry = defenses.get(`${site}|${team}`);

      if (!entry && !team) {

        const candidates = byName.get(`${site}|${name}|${pos}`);

        if (candidates?.length === 1) entry = candidates[0];

      }

      const available = Boolean(eligible(entry));

      if (available && (selectedSite === "both" || selectedSite === site)) included = true;

      merged[`${site}_available`] = available;

      merged[`${site}_id`] = available ? String(entry[idKey]) : null;

      merged[`${site}_sal`] = available ? num(entry[salaryKey]) : null;

      const projection = num(merged[`${site}_proj`]);

      merged[`${site}_val`] = available && projection != null ? projection / (merged[`${site}_sal`] / 1000) : null;

      // Avoid displaying metrics for a site where this player cannot be entered.

      if (!available) for (const metric of ["proj", "pown", "floor", "ceiling", "opt", "lev", "rtg"]) merged[`${site}_${metric}`] = null;

    }

    return included ? merged : null;

  }).filter(Boolean);

}

function makeGameOptions(rows, vegasRows) {

  const available = new Set(rows.map(gameKey).filter(Boolean));

  const options = new Map();

  for (const row of vegasRows) {

    const team = canonicalTeam(row.team);

    const opp = canonicalTeam(row.opponent ?? row.opp);

    const key = gameKey({ team, opp });

    if (!available.has(key) || options.has(key)) continue;

    const home = canonicalTeam(row.field);

    const label = home === team ? `${opp} @ ${team}` : home === opp ? `${team} @ ${opp}` : `${team} vs ${opp}`;

    const parsed = row.gameDate ? new Date(row.gameDate).getTime() : NaN;

    options.set(key, { key, label, kickoff: Number.isFinite(parsed) ? parsed : null });

  }

  for (const key of available) if (!options.has(key)) options.set(key, { key, label: key.replace("@", " vs "), kickoff: null });

  return [...options.values()].sort((a, b) => {

    if (a.kickoff == null && b.kickoff != null) return 1;

    if (a.kickoff != null && b.kickoff == null) return -1;

    return (a.kickoff != null && b.kickoff != null ? a.kickoff - b.kickoff : 0) || a.label.localeCompare(b.label);

  });

}

const escapeCSV = (v) => /[",\r\n]/.test(String(v ?? "")) ? `"${String(v ?? "").replace(/"/g, '""')}"` : String(v ?? "");

const formatValue = (col, value) => col.type === "money" ? fmt0(value) : col.type === "pct" ? fmtPct(value) : col.type.startsWith("num1") ? fmt1(value) : value ?? "";

function downloadCSV(rows, columns, filename) {

  const text = [columns.map((c) => escapeCSV(c.label)).join(","), ...rows.map((r) => columns.map((c) => escapeCSV(formatValue(c, r[c.key]))).join(","))].join("\n");

  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8;" }));

  const anchor = document.createElement("a");

  anchor.href = url; anchor.download = filename; anchor.click();

  URL.revokeObjectURL(url);

}

function TeamsDropdown({ allTeams, selected, onChange, isDark }) {

  const selectedSet = new Set(selected);

  return (

    <details className="relative z-40">

      <summary className="list-none cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400">

        <span className={`inline-flex h-8 items-center px-3 rounded-md border text-xs ${isDark ? "bg-slate-900/80 text-slate-100 border-slate-700/80" : "bg-white text-slate-700 border-slate-300"}`}>Teams ({selected.length}/{allTeams.length})</span>

      </summary>

      <div className={`absolute mt-2 z-50 border rounded-2xl shadow-xl p-3 w-[260px] ${isDark ? "bg-slate-950 border-slate-800/90" : "bg-white border-slate-200"}`}>

        <div className="flex items-center justify-between mb-2">

          <button type="button" className="text-xs px-2 py-1 rounded-md border" onClick={() => onChange([...allTeams])}>Select all</button>

          <button type="button" className="text-xs px-2 py-1" onClick={() => onChange([])}>Clear</button>

        </div>

        <div className="grid grid-cols-4 gap-1 max-h-64 overflow-auto pr-1">

          {allTeams.map((team) => <label key={team} className="flex items-center gap-1 text-xs">

            <input type="checkbox" className="accent-sky-500" checked={selectedSet.has(team)} onChange={() => {

              const next = new Set(selected); next.has(team) ? next.delete(team) : next.add(team); onChange([...next]);

            }} /><span>{team}</span>

          </label>)}

        </div>

      </div>

    </details>

  );

}

function dirForKey(key) {

  if (/^(dk|fd)_(proj|val|opt|lev|rtg|floor|ceiling)$/.test(key)) return "higher";

  return /^(dk|fd)_pown$/.test(key) ? "lower" : null;

}

function heatColor(stat, value, isDark) {

  if (value == null || !stat || stat.min === stat.max) return null;

  let t = Math.max(0, Math.min(1, (value - stat.min) / (stat.max - stat.min)));

  if (stat.dir === "lower") t = 1 - t;

  return `hsl(${t * 120}, ${isDark ? 85 : 85}%, ${isDark ? 62 : t < 0.5 ? 38 : 30}%)`;

}

const keynorm = (s) => String(s || "").toLowerCase().trim().replace(/\s+/g, " ").replace(/[^a-z0-9 ]/g, "");

function buildPointIndex(raw) {

  const pick = (obj) => {

    const index = {};

    for (const [rawKey, entry] of Object.entries(obj || {})) {

      const parts = rawKey.split("|").map((v) => v.trim());

      const team = canonicalTeam(entry?.team || (parts.length >= 2 ? parts[0] : ""));

      const player = entry?.player || (parts.length >= 2 ? parts[1] : rawKey);

      const slot = entry?.slot || (parts.length >= 3 ? parts[2] : "");

      const bins = Array.isArray(entry) ? entry : Array.isArray(entry?.bins) ? entry.bins : null;

      if (!bins) continue;

      for (const value of [player, `${team} ${player}`, `${team}|${player}|${slot}`, rawKey]) {

        const key = keynorm(value); if (key && !index[key]) index[key] = bins;

      }

    }

    return index;

  };

  return { dk: pick(raw?.dk ?? raw?.DK), fd: pick(raw?.fd ?? raw?.FD) };

}

function PointDistModal({ open, onClose, isDark, site, row, bins }) {

  // Hooks must run on every render, including when the modal is closed.

  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);

  useEffect(() => {

    const resize = () => setIsMobile(window.innerWidth < 640);

    window.addEventListener("resize", resize);

    return () => window.removeEventListener("resize", resize);

  }, []);

  useEffect(() => {

    if (!open) return undefined;

    const escape = (event) => { if (event.key === "Escape") onClose(); };

    window.addEventListener("keydown", escape);

    return () => window.removeEventListener("keydown", escape);

  }, [open, onClose]);

  const chartData = useMemo(() => (bins || []).map((b, idx) => {

    const a = num(b?.bin_start); const z = num(b?.bin_end); const p = num(b?.prob);

    return { label: a != null && z != null ? `${a}-${z}` : `bin-${idx + 1}`, shortLabel: a != null ? String(a) : String(idx + 1), mid: a != null && z != null ? (a + z) / 2 : idx, probPct: p != null ? p * 100 : 0 };

  }), [bins]);

  const xDomain = useMemo(() => {

    const mids = chartData.map((d) => d.mid).sort((a, b) => a - b);

    if (!mids.length) return [0, 1];

    if (mids.length === 1) return [mids[0] - 2.5, mids[0] + 2.5];

    return [mids[0] - (Math.abs(mids[1] - mids[0]) || 1) / 2, mids[mids.length - 1] + (Math.abs(mids[mids.length - 1] - mids[mids.length - 2]) || 1) / 2];

  }, [chartData]);

  const ticks = useMemo(() => chartData.filter((_, i) => i % (isMobile ? 4 : 2) === 0).map((d) => d.mid), [chartData, isMobile]);

  if (!open) return null;

  const proj = num(row?.[`${site.toLowerCase()}_proj`]);

  return <div className="fixed inset-0 z-[200] flex items-center justify-center p-2 sm:p-4" role="dialog" aria-modal="true" aria-label={`${row?.player || "Player"} fantasy points distribution`}>

    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

    <div className={`relative w-full max-w-3xl max-h-[95vh] rounded-2xl border shadow-2xl overflow-y-auto ${isDark ? "border-slate-700/60 bg-slate-950 text-slate-100" : "border-slate-200 bg-white text-slate-900"}`}>

      <div className="flex items-start justify-between gap-3 px-3 pt-4 sm:px-5 sm:pt-5">

        <div className="min-w-0">

          <div className="text-xs text-slate-400">Fantasy Points Distribution</div>

          <div className="mt-1 flex flex-wrap items-center gap-2">

            <div className="max-w-[190px] truncate text-base font-extrabold tracking-tight sm:max-w-none sm:text-lg">{row?.player || "—"}</div>

            <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold border ${site === "DK" ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" : "bg-sky-500/15 text-sky-500 border-sky-500/30"}`}>{site}</span>

            {proj != null && <span className="text-xs">Proj: <span className="font-semibold tabular-nums">{proj.toFixed(1)}</span></span>}

          </div>

        </div>

        <button type="button" className={`shrink-0 rounded-xl border px-3 py-2 text-xs font-semibold ${isDark ? "border-slate-700 bg-slate-900/40 hover:bg-slate-900" : "border-slate-200 bg-slate-50 hover:bg-slate-100"}`} onClick={onClose}>Close</button>

      </div>

      <div className="px-2 pb-4 pt-3 sm:px-5 sm:pb-5 sm:pt-4">

        {!chartData.length ? <div className="text-sm">No distribution data found for this player yet.</div> :

          <div className={isMobile ? "h-[360px] w-full" : "h-[320px] w-full"}>

            <ResponsiveContainer width="100%" height="100%">

              <BarChart data={chartData} margin={{ top: 10, right: 18, left: 8, bottom: isMobile ? 42 : 18 }} barCategoryGap={isMobile ? "12%" : "8%"}>

                <CartesianGrid stroke={isDark ? "#334155" : "#e5e7eb"} strokeDasharray="3 3" />

                <XAxis type="number" dataKey="mid" domain={xDomain} ticks={ticks} tickFormatter={(v) => chartData.find((d) => d.mid === v)?.shortLabel ?? String(Math.round(v))} tick={{ fontSize: isMobile ? 11 : 12, fill: isDark ? "#cbd5f5" : "#334155" }} axisLine={{ stroke: isDark ? "#475569" : "#cbd5e1" }} tickLine={false} angle={isMobile ? -35 : 0} textAnchor={isMobile ? "end" : "middle"} height={isMobile ? 52 : 30} />

                <YAxis width={isMobile ? 38 : 60} tickFormatter={(v) => `${v}%`} tick={{ fontSize: isMobile ? 10 : 11, fill: isDark ? "#cbd5f5" : "#334155" }} axisLine={{ stroke: isDark ? "#475569" : "#cbd5e1" }} tickLine={false} />

                <Tooltip cursor={{ fill: "rgba(56,189,248,0.12)" }} contentStyle={{ backgroundColor: isDark ? "#020617" : "#ffffff", border: "1px solid #38bdf8", borderRadius: 10, fontSize: 12 }} labelStyle={{ color: isDark ? "#e2e8f0" : "#0f172a" }} formatter={(v) => [`${Number(v).toFixed(2)}%`, "Probability"]} labelFormatter={(_, payload) => payload?.[0]?.payload?.label ? `Bin ${payload[0].payload.label}` : ""} />

                {proj != null && <ReferenceLine x={proj} stroke="#38bdf8" strokeWidth={2} strokeDasharray="6 4" />}

                <Bar dataKey="probPct" radius={[6, 6, 0, 0]} fill="#7dd3fc" stroke="#38bdf8" strokeWidth={1} />

              </BarChart>

            </ResponsiveContainer>

          </div>}

      </div>

    </div>

  </div>;

}

export default function NflProjections() {

  const simulations = useJson(SOURCE);

  const supplemental = useJson(SUPPLEMENTAL_PROJECTIONS);

  const sites = useJson(SITE_IDS);

  const vegas = useJson(VEGAS);

  const { data: meta } = useAnyJson(META);

  const { data: pointDistsRaw } = useAnyJson(POINT_DISTS);

  const loading = simulations.loading || sites.loading || vegas.loading;

  const err = simulations.err || sites.err || vegas.err;

  const [site, setSite] = useState(() => queryChoice("site", ["both", "dk", "fd"], "both"));

  const [slate, setSlate] = useState(() => queryChoice("slate", SLATES.map((s) => s.id), "main"));

  const [q, setQ] = useState("");

  const [theme, setTheme] = useState("dark");

  const isDark = theme === "dark";

  const [posSel, setPosSel] = useState([...POSITIONS]);

  const [selectedGame, setSelectedGame] = useState("ALL");

  const [teamSelection, setTeamSelection] = useState({ scope: "", selected: null });

  const scope = `${slate}|${site}`;

  const [modal, setModal] = useState(null);

  const [sort, setSort] = useState({ key: site === "fd" ? "fd_proj" : "dk_proj", dir: "desc" });

  useEffect(() => {

    const params = new URLSearchParams(window.location.search);

    params.set("slate", slate); params.set("site", site);

    window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}${window.location.hash}`);

  }, [slate, site]);

  const rows = useMemo(() => wireRows(simulations.rows, sites.rows, vegas.rows, slate, site, supplemental.rows), [simulations.rows, sites.rows, vegas.rows, slate, site, supplemental.rows]);

  const allTeams = useMemo(() => [...new Set(rows.map((r) => r.team).filter(Boolean))].sort(), [rows]);

  // null means all teams; an explicit empty list means none. New slate/site defaults to all.

  const teamsSel = teamSelection.scope === scope && teamSelection.selected != null

    ? teamSelection.selected.filter((team) => allTeams.includes(team)) : allTeams;

  const gameOptions = useMemo(() => makeGameOptions(rows, vegas.rows), [rows, vegas.rows]);

  const activeGame = gameOptions.some((game) => game.key === selectedGame) ? selectedGame : "ALL";

  useEffect(() => { if (!loading && selectedGame !== activeGame) setSelectedGame("ALL"); }, [loading, selectedGame, activeGame]);

  const columns = useMemo(() => site === "dk" ? [...COLS_COMMON, ...COLS_DK] : site === "fd" ? [...COLS_COMMON, ...COLS_FD] : [

    ...COLS_COMMON, COLS_DK[0], COLS_FD[0], ...COLS_DK.slice(1), ...COLS_FD.slice(1),

  ], [site]);

  const activeSort = columns.some((col) => col.key === sort.key) ? sort : { key: site === "fd" ? "fd_proj" : "dk_proj", dir: "desc" };

  const filtered = useMemo(() => {

    const needle = q.trim().toLowerCase();

    const teamSet = new Set(teamsSel); const posSet = new Set(posSel);

    return rows.filter((r) => posSet.has(r.pos) && teamSet.has(r.team)

      && (activeGame === "ALL" || gameKey(r) === activeGame)

      && (!needle || `${r.player || ""} ${r.pos} ${r.team} ${r.opp}`.toLowerCase().includes(needle)));

  }, [rows, q, posSel, teamsSel, activeGame]);

  const sorted = useMemo(() => {

    const { key, dir } = activeSort; const sign = dir === "asc" ? 1 : -1;

    const text = columns.find((col) => col.key === key)?.type === "text";

    return [...filtered].sort((a, b) => {

      if (text) return String(a[key] ?? "").localeCompare(String(b[key] ?? "")) * sign;

      const av = num(a[key]); const bv = num(b[key]);

      if (av == null && bv == null) return 0;

      if (av == null) return 1; if (bv == null) return -1;

      return (av - bv) * sign;

    });

  }, [filtered, columns, activeSort.key, activeSort.dir]);

  const heatStats = useMemo(() => {

    const stats = {};

    for (const col of columns) {

      const dir = dirForKey(col.key); if (!dir) continue;

      const values = sorted.map((r) => num(r[col.key])).filter((v) => v != null);

      if (values.length) stats[col.key] = { min: Math.min(...values), max: Math.max(...values), dir };

    }

    return stats;

  }, [sorted, columns]);

  const pointIndex = useMemo(() => buildPointIndex(pointDistsRaw), [pointDistsRaw]);

  const modalRow = modal ? rows.find((r) => playerKey(r) === modal.key) || modal.row : null;

  const modalBins = modal ? pointIndex[modal.site.toLowerCase()]?.[keynorm(`${modalRow.team} ${modalRow.player}`)]

    || pointIndex[modal.site.toLowerCase()]?.[keynorm(modalRow.player)] : null;

  const updatedStamp = useMemo(() => {

    const artifact = meta?.artifacts?.find((item) => String(item.path || "").replace(/^\//, "").endsWith(SOURCE.replace(/^\//, "")));

    const raw = artifact?.last_updated || artifact?.tags?.last_updated || meta?.last_updated;

    if (!raw) return null;

    const date = new Date(raw);

    return Number.isFinite(date.getTime()) ? date.toLocaleString(undefined, { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" }) : null;

  }, [meta]);

  const control = `h-8 min-w-0 rounded-md border px-3 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 ${isDark ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500" : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"}`;

  const cardClass = isDark ? "rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm" : "rounded-2xl border border-slate-200 bg-white shadow-sm";

  const headerBg = isDark ? "bg-slate-900 text-slate-200" : "bg-slate-50 text-slate-700";
  const tabWrapClass = "inline-flex shrink-0 items-center gap-6 border-b " + (isDark ? "border-slate-800" : "border-slate-200");
  const tabClass = (selected) => "relative inline-flex h-9 items-center gap-2 whitespace-nowrap pb-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 " + (selected ? (isDark ? "text-white" : "text-slate-900") + " after:absolute after:left-0 after:right-0 after:-bottom-px after:h-0.5 after:rounded-full after:bg-sky-400" : "text-slate-500 hover:text-sky-500");


  return <div className={isDark ? "min-h-screen bg-slate-950 text-slate-100" : "min-h-screen bg-slate-100 text-slate-900"}>

    <div className="w-full px-4 md:px-6 py-5">

      <header className="mb-3 flex flex-wrap items-center justify-between gap-x-5 gap-y-3">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">NFL — DFS Projections</h1>
          <span className={`text-xs italic ${isDark ? "text-slate-400" : "text-slate-600"}`}>Updated: {updatedStamp ?? "—"}</span>
        </div>
          <div className={`inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 ${isDark ? "bg-slate-900/90 ring-slate-700/80" : "bg-white ring-slate-300"}`}>

            {["dark", "light"].map((value) => <button key={value} type="button" onClick={() => setTheme(value)} className={`px-3 py-1 rounded-full font-medium transition ${theme === value ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-500"}`}>{value === "dark" ? "Dark" : "Light"}</button>)}

          </div>
      </header>

      {supplemental.err && <p className="mb-3 text-xs text-slate-400">Supplemental projections could not load. Players and simulation metrics remain available; some additional metrics may be blank.</p>}

      <section aria-label="Slate and site" className={cardClass + " mb-3 p-3"}>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className={tabWrapClass}>

            {["dk", "fd", "both"].map((value) => <button key={value} type="button" aria-label={SITES[value].label} aria-pressed={site === value} onClick={() => { setSite(value); setSelectedGame("ALL"); setSort({ key: value === "fd" ? "fd_proj" : "dk_proj", dir: "desc" }); }} className={tabClass(site === value)}>

              {value !== "both" && <img src={SITES[value].logo} alt={SITES[value].label} className="w-4 h-4" />}<span>{SITES[value].label}</span>

            </button>)}

          </div>

          <label className="flex min-w-0 items-center gap-3 text-xs">Slate <select aria-label="Slate" className={control} value={slate} onChange={(event) => { setSlate(event.target.value); setSelectedGame("ALL"); }}>{SLATES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select></label>

        </div>
      </section>

      <section aria-label="Projections" className={cardClass}>
        <div className={`border-b p-3 ${isDark ? "border-slate-800" : "border-slate-200"}`}>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h2 className="text-sm font-semibold">Player Projections</h2>
              <span className={`text-xs ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                {!loading && !err && <>{sorted.length.toLocaleString()} of {rows.length.toLocaleString()} players · {SLATES.find((s) => s.id === slate)?.label}</>}
              </span>
            </div>
          <button type="button" className="h-8 shrink-0 px-3 rounded-md bg-sky-500 text-slate-950 text-xs font-semibold hover:bg-sky-400 shadow-sm" onClick={() => downloadCSV(sorted, columns, `nfl_${slate}_${site}_projections.csv`)}>Export CSV</button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap items-center gap-3">

            {POSITIONS.map((p) => <button key={p} type="button" aria-pressed={posSel.includes(p)} onClick={() => setPosSel((prev) => prev.includes(p) ? prev.filter((v) => v !== p) : [...prev, p])} className={`relative h-8 pb-2 text-xs font-semibold transition ${posSel.includes(p) ? "text-sky-500 after:absolute after:left-0 after:right-0 after:-bottom-px after:h-0.5 after:bg-sky-400" : "text-slate-500 hover:text-sky-500"}`}>{p}</button>)}

          </div>

          <div className="shrink-0"><TeamsDropdown allTeams={allTeams} selected={teamsSel} isDark={isDark} onChange={(selected) => setTeamSelection({ scope, selected })} /></div>

          <label className="flex items-center gap-2 text-xs">Game <select aria-label="Game" className={control} value={activeGame} onChange={(event) => setSelectedGame(event.target.value)}><option value="ALL">All Games</option>{gameOptions.map((game) => <option key={game.key} value={game.key}>{game.label}</option>)}</select></label>

          <input className={`${control} w-full sm:ml-auto sm:w-64`} aria-label="Search player, team, or opponent" placeholder="Search player / team / opp…" value={q} onChange={(event) => setQ(event.target.value)} />



          </div>
        </div>

        <div className="overflow-x-auto overflow-y-auto h-[70vh] rounded-b-2xl overscroll-contain">

          <table className="min-w-full w-max border-separate text-[11px] md:text-[12px]" style={{ borderSpacing: 0 }}>

            <thead className={`${headerBg} sticky top-0 z-20 shadow-[0_2px_0_0_rgba(0,0,0,0.35)]`}>

              <tr>{columns.map((col) => <th key={col.key} className={`px-2 py-1 font-semibold text-center whitespace-nowrap cursor-pointer select-none ${col.w || ""} ${headerBg} ${col.key === "player" ? "sticky left-0 z-30" : ""}`} onClick={() => setSort({ key: col.key, dir: activeSort.key === col.key && activeSort.dir === "desc" ? "asc" : "desc" })} title="Click to sort">

                <div className="inline-flex items-center gap-1"><span>{col.label}</span><span className="text-[10px] text-slate-400">{activeSort.key === col.key ? activeSort.dir === "desc" ? "▼" : "▲" : "▲"}</span></div>

              </th>)}</tr>

            </thead>

            <tbody>

              {loading && <tr><td className="px-2 py-1 text-center text-slate-400" colSpan={columns.length}>Loading…</td></tr>}

              {err && <tr><td className="px-2 py-1 text-center text-red-400" colSpan={columns.length}>Failed to load: {err}</td></tr>}

              {!loading && !err && !sorted.length && <tr><td className="px-2 py-4 text-center text-slate-400" colSpan={columns.length}>No players match the selected slate and filters.</td></tr>}

              {!loading && !err && sorted.map((row, index) => {

                const rowBg = isDark ? index % 2 === 0 ? "bg-slate-950" : "bg-slate-900" : index % 2 === 0 ? "bg-white" : "bg-gray-50";

                return <tr key={`${playerKey(row)}|${index}`} className={rowBg}>{columns.map((col) => {

                  if (col.key === "player") return <td key={col.key} className={`px-2 py-1 text-left sticky left-0 z-10 ${rowBg} shadow-[inset_-6px_0_8px_-6px_rgba(0,0,0,0.6)]`}><div className="flex items-center gap-2">{row.team && <img src={teamLogo(row.team)} alt="" className="w-4 h-4 rounded-sm object-contain" onError={(event) => { event.currentTarget.style.visibility = "hidden"; }} />}<span className="whitespace-nowrap">{row.player}</span></div></td>;

                  const color = heatColor(heatStats[col.key], num(row[col.key]), isDark);

                  const style = color ? { color, fontWeight: 600 } : undefined;

                  const value = formatValue(col, row[col.key]);

                  const isProjection = col.key === "dk_proj" || col.key === "fd_proj";

                  return <td key={col.key} className={`px-2 py-1 text-center ${col.type !== "text" ? "tabular-nums" : ""}`} style={style}>

                    {isProjection && num(row[col.key]) != null ? <button type="button" className="group relative w-full text-center font-semibold rounded-md px-1 py-0.5 underline underline-offset-4 decoration-sky-400/70 hover:decoration-sky-300 hover:bg-sky-500/10 transition cursor-pointer" style={style} title="Click to open point distribution chart" onClick={() => setModal({ key: playerKey(row), row, site: col.key === "dk_proj" ? "DK" : "FD" })}>{value}</button> : value}

                  </td>;

                })}</tr>;

              })}

            </tbody>

          </table>

        </div>

      </section>

    </div>

    <PointDistModal open={Boolean(modal)} onClose={() => setModal(null)} isDark={isDark} site={modal?.site || "DK"} row={modalRow} bins={modalBins} />

  </div>;

}
